/**
 * Crop images are served from the FULL shelf photo: the viz URL points at the source
 * image and carries the model's crop box as `crop=left,top,width,height` (see
 * `ProductEmbeddingCrop.public_viz_url` in the backend). That lets us ask the image
 * service for a wider window and have it draw the crop bounds, so labelers can see
 * what surrounds a crop instead of a floating cutout. The `box` param is the same one
 * the produce-vision task creator already uses, and is interpreted in source-image
 * coordinates, so it needs no adjustment when the window is padded or clamped.
 *
 * `crop.imageUrl` stays the tight crop everywhere else on purpose — it is uploaded as
 * a product image and stored as a design-group preview, and neither wants context.
 */
export const CROP_CONTEXT_PADDING_PX = 50;

const BOX_STROKE_WIDTH_PX = 4;
/** Forge accent, the same colour the draft-crop detection overlay uses. */
const BOX_COLOR = 'F97316';
const BOX_FILL_OPACITY = 0;

export interface CropBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

function parseCropParam(value: string): CropBox | null {
  const parts = value.split(',');
  if (parts.length !== 4) return null;
  const numbers = parts.map((part) => Number(part));
  if (numbers.some((n) => !Number.isFinite(n))) return null;
  const [left, top, width, height] = numbers;
  if (width <= 0 || height <= 0) return null;
  return { left, top, width, height };
}

/** The model's crop bounds in source-image coordinates, or null for a non-crop URL. */
export function parseCropBox(imageUrl: string | null | undefined): CropBox | null {
  if (!imageUrl) return null;
  const queryStart = imageUrl.indexOf('?');
  if (queryStart === -1) return null;
  const cropParam = imageUrl
    .slice(queryStart + 1)
    .split('&')
    .find((param) => param.startsWith('crop='));
  return cropParam ? parseCropParam(cropParam.slice('crop='.length)) : null;
}

/**
 * Widen a crop URL by `padding` source pixels on every side and draw the model's crop
 * bounds on top. URLs we cannot read a crop box out of (draft placeholders, product
 * images, already-widened URLs) are returned untouched.
 */
export function cropContextImageUrl(
  imageUrl: string | null | undefined,
  padding: number = CROP_CONTEXT_PADDING_PX
): string {
  if (!imageUrl) return '';
  if (padding <= 0) return imageUrl;
  // A drawn box means this URL has already been widened; padding it again would
  // zoom out on every render pass that re-derives it.
  if (imageUrl.includes('?box=') || imageUrl.includes('&box=')) return imageUrl;
  const box = parseCropBox(imageUrl);
  if (!box) return imageUrl;

  const queryStart = imageUrl.indexOf('?');
  const base = imageUrl.slice(0, queryStart);
  // Keep every other param (notably transformation_matrix) byte-for-byte: re-encoding
  // the matrix through URLSearchParams would change the string the CDN caches on.
  const otherParams = imageUrl
    .slice(queryStart + 1)
    .split('&')
    .filter((param) => param && !param.startsWith('crop=') && !param.startsWith('box='));

  // The source size is unknown here, so only the left/top edges can be clamped; the
  // image service already tolerates a window that runs past the right/bottom edge.
  const left = Math.max(0, Math.round(box.left - padding));
  const top = Math.max(0, Math.round(box.top - padding));
  const width = Math.round(box.width) + (Math.round(box.left) - left) + padding;
  const height = Math.round(box.height) + (Math.round(box.top) - top) + padding;

  const cropParam = `crop=${left},${top},${width},${height}`;
  const boxParam = `box=${Math.round(box.left)},${Math.round(box.top)},${Math.round(
    box.width
  )},${Math.round(box.height)},${BOX_STROKE_WIDTH_PX},${BOX_COLOR},${BOX_FILL_OPACITY}`;

  return `${base}?${[...otherParams, cropParam, boxParam].join('&')}`;
}
